import { ArrowRight, ShieldCheck, X } from 'lucide-react'
import { useState } from 'react'
import {
	ScreeningSectionCard,
	ScreeningStepContainer,
} from '#app/components/screening/ScreeningStepContainer'

/**
 * Demo-only ID verification step.
 *
 * Renders the SAME intro card the real screening uses (via
 * ScreeningStepContainer / ScreeningSectionCard), but instead of creating a
 * real Veriff session it plays a guidance video with a Skip button that
 * advances the flow. No session, no webhook, no DB.
 */
export function DemoIdVerificationStep({
	onNext,
	onBack,
}: {
	onNext: () => void
	onBack?: () => void
}) {
	const [launching, setLaunching] = useState(false)
	return (
		<>
			<ScreeningStepContainer
				title="Verify Identity"
				onBack={onBack}
				onNext={() => setLaunching(true)}
				canGoNext={true}
				nextButtonText="Start Verification"
			>
				<div className="space-y-4 sm:space-y-6">
					<ScreeningSectionCard>
						<div className="space-y-4">
							<div className="flex items-start text-[16px] text-[#32302F]">
								<img
									src="/img/ListItemsIconGreen.png"
									alt="Info"
									className="mt-0.5 mr-2 inline-block"
								/>
								<p>
									<span className="font-[500]">THE ID MUST BELONG TO YOU</span>{' '}
									(Do not complete this process on behalf of someone else).
								</p>
							</div>
							<div className="flex items-start text-[16px] text-[#32302F]">
								<img
									src="/img/ListItemsIconGreen.png"
									alt="Info"
									className="mt-0.5 mr-2 inline-block"
								/>
								<p>
									You will need a valid government issued ID (drivers license,
									passport or residence permit)
								</p>
							</div>
							<div className="flex items-start text-[16px] text-[#32302F]">
								<img
									src="/img/ListItemsIconGreen.png"
									alt="Info"
									className="mt-0.5 mr-2 inline-block"
								/>
								<p>Make sure you are in a well-lit area.</p>
							</div>
						</div>
					</ScreeningSectionCard>
				</div>
			</ScreeningStepContainer>
			{launching && (
				<SimulatedVeriffOverlay
					onComplete={onNext}
					onCancel={() => setLaunching(false)}
				/>
			)}
		</>
	)
}

function SimulatedVeriffOverlay({
	onComplete,
	onCancel,
}: {
	onComplete: () => void
	onCancel: () => void
}) {
	return (
		<div className="fixed inset-0 z-60 flex flex-col bg-white">
			{/* Top bar */}
			<div className="flex shrink-0 items-center justify-between border-b border-[#EDEDEB] px-5 py-4">
				<div className="flex items-center gap-2 text-[#32302F]">
					<ShieldCheck className="h-5 w-5" />
					<span className="text-[15px] font-semibold">
						Veriff · Identity Verification
					</span>
				</div>
				<button
					type="button"
					onClick={onCancel}
					className="rounded-full p-1 hover:bg-gray-100"
				>
					<X className="h-5 w-5 text-[#4F4F4F]" />
				</button>
			</div>

			{/* Video */}
			<div className="flex flex-1 flex-col items-center justify-center px-4 py-6 sm:px-8">
				<div
					className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-black"
					style={{ aspectRatio: '16/9' }}
				>
					<iframe
						src="https://www.youtube.com/embed/CTfQfBeSA2U?autoplay=1"
						title="ID Verification Guide"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
						allowFullScreen
						className="absolute inset-0 h-full w-full"
					/>
				</div>

				{/* Skip */}
				<button
					type="button"
					onClick={onComplete}
					className="mt-6 flex items-center gap-2 rounded-[30px] border border-[#CCCCCC] bg-white px-6 py-2.5 text-[14px] font-medium text-[#484848] transition-colors hover:bg-gray-50"
				>
					Skip Video
					<ArrowRight className="h-4 w-4" />
				</button>
			</div>

			<p className="shrink-0 pb-4 text-center text-[12px] text-[#9CA3AF]">
				Demo mode — no data is captured or stored.
			</p>
		</div>
	)
}
